import React, { useMemo, useState } from "react";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import {
  ClearIndicatorProps,
  components,
  ControlProps,
  CSSObjectWithLabel,
  type DropdownIndicatorProps,
  GroupBase,
  InputProps,
  MultiValue,
  MultiValueProps,
  MultiValueProps as ReactSelectMultiValueProps,
  MultiValueRemoveProps,
  MultiValueRemoveProps as ReactSelectMultiValueRemoveProps,
  OptionProps,
  OptionProps as ReactSelectOptionProps,
  ValueContainerProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import { useIsMobileWidth } from "../Guest/utils";
import {
  OptionContainer,
  SelectAllSeparator,
  sizeVariants,
  styledCreatableSelect,
} from "./__styles__/MultiSelect";
import Wrapper from "./Wrapper";

export interface BaseOption {
  value: string;
  label: string;
  _isOverflowChip?: boolean;
}

interface ExtendedMultiValueProps<T>
  extends MultiValueProps<T, true, GroupBase<T>> {
  index: number;
}

export interface MultiSelectProps<T extends BaseOption> {
  name: string;
  value: string[];
  onChange: (value: string[] | T[] | null) => void;
  options: T[];
  disabled?: boolean;
  tooltip?: string;
  helperText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  loading?: boolean;
  onInputChange?: (value: string) => void;
  renderOption?: (option: T, props: OptionProps<T, true>) => React.ReactNode;
  formatOptionLabel?: (option: T) => string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  filterOption?: (
    option: { label: string; value: string; data: T },
    inputValue: string,
  ) => boolean;
  useBaseOption?: boolean;
  customComputeValue?: (value: string[]) => T[];
  size?: keyof typeof sizeVariants;
}

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const createSelectAllOption = <T extends BaseOption>(label: string): T =>
  ({
    value: SELECT_ALL_VALUE,
    label,
  }) as T;

const DefaultOption = <T extends BaseOption>(props: OptionProps<T, true>) => {
  return (
    <components.Option {...props}>
      <OptionContainer>{props.data.label}</OptionContainer>
    </components.Option>
  );
};

const SelectAllOption = <T extends BaseOption>(props: OptionProps<T, true>) => {
  return (
    <>
      <components.Option {...props}>
        <OptionContainer>
          <Body size="large" type="regular">
            {props.data.label}
          </Body>
        </OptionContainer>
      </components.Option>
      <SelectAllSeparator />
    </>
  );
};

const CustomOption = <T extends BaseOption>(
  optionProps: OptionProps<T, true>,
  renderOption?: (option: T, props: OptionProps<T, true>) => React.ReactNode,
): React.ReactElement => {
  const option = optionProps.data;

  if (option.value === SELECT_ALL_VALUE) {
    return SelectAllOption(optionProps);
  }

  if (renderOption) {
    const rendered = renderOption(option, optionProps);
    return rendered as React.ReactElement;
  }

  return DefaultOption(optionProps);
};

const CustomMultiValue = <T extends BaseOption>(
  props: MultiValueProps<T, true> & {
    index: number;
    menuIsOpen: boolean;
    maxOptionChips: number;
    formatOptionLabel: (option: T) => string;
  },
) => {
  const { getValue, menuIsOpen, maxOptionChips, formatOptionLabel } = props;
  const chipPosition = props.index;
  const totalSelectedItems = getValue();
  const overflow = totalSelectedItems.length - maxOptionChips;

  if (!menuIsOpen && chipPosition > maxOptionChips) {
    return null;
  }

  const overflowChip = !menuIsOpen && chipPosition === maxOptionChips;
  const content = overflowChip ? `+${overflow}` : formatOptionLabel(props.data);

  const newProps = {
    ...props,
    data: {
      ...props.data,
      _isOverflowChip: overflowChip,
    },
  } as MultiValueProps<T, true>;

  return (
    <components.MultiValue {...newProps}>
      <Body size="small" type="emphasis" style={{ marginBottom: "0px" }}>
        {content}
      </Body>
    </components.MultiValue>
  );
};

const MultiValueRemove = <T extends BaseOption>(
  props: MultiValueRemoveProps<T, true>,
) => {
  if (props.data._isOverflowChip || props.selectProps.isDisabled) return null;

  return (
    <components.MultiValueRemove {...props}>
      <Icon iconName="x" color="contentSecondary" size={12} />
    </components.MultiValueRemove>
  );
};

const ClearIndicator = <T extends BaseOption>(
  props: ClearIndicatorProps<T, true>,
) => {
  return (
    <components.ClearIndicator {...props}>
      <Icon iconName="x" color="contentDisabled" size={16} />
    </components.ClearIndicator>
  );
};

const CustomDropdownIndicator = <T extends BaseOption>(
  props: DropdownIndicatorProps<T, true>,
) => {
  if (props.selectProps.isDisabled) return null;

  return (
    <components.DropdownIndicator {...props}>
      <Icon iconName="chevron-down" color="contentSecondary" size={16} />
    </components.DropdownIndicator>
  );
};

const MultiSelect = <T extends BaseOption>({
  name,
  value,
  onChange,
  options,
  disabled,
  tooltip,
  helperText,
  placeholder,
  label,
  required,
  error,
  loading = false,
  onInputChange,
  renderOption,
  formatOptionLabel,
  filterOption,
  useBaseOption = true,
  customComputeValue,
  size = "medium",
  showSelectAll = true,
  selectAllLabel = "Select all",
  ...props
}: MultiSelectProps<T>) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isMobileWidth = useIsMobileWidth();
  const maxOptionChips = isMobileWidth ? 1 : 3;

  const defaultFormatOptionLabel = (option: T) => option.label;
  const actualFormatOptionLabel = formatOptionLabel ?? defaultFormatOptionLabel;

  const optionsWithSelectAll = useMemo(() => {
    if (options.length > 0 && showSelectAll) {
      const selectAllOption = createSelectAllOption<T>(selectAllLabel);
      return [selectAllOption, ...options];
    }
    return options;
  }, [options, showSelectAll, selectAllLabel]);

  const handleChange = (selectedValues: MultiValue<T>) => {
    if (!selectedValues) {
      onChange(null);
      return;
    }

    const selectAllOption = selectedValues.find(
      option => option.value === SELECT_ALL_VALUE,
    );

    if (selectAllOption) {
      const regularOptions = options.filter(
        option => option.value !== SELECT_ALL_VALUE,
      );

      useBaseOption ?
        onChange(regularOptions.map(option => option.value))
      : onChange(regularOptions);
    } else {
      const filteredValues = selectedValues.filter(
        option => option.value !== SELECT_ALL_VALUE,
      );

      useBaseOption ?
        onChange(filteredValues.map(option => option.value))
      : onChange(filteredValues);
    }
  };

  const handleInputChange = (newValue: string) => {
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  // Convert string values back to option objects for react-select
  const computeValue = () => {
    if (customComputeValue) {
      return customComputeValue(value);
    }

    if (!value || !Array.isArray(value)) {
      return [];
    }

    const output = value
      .map(val => options.find(opt => opt.value === val))
      .filter(Boolean) as T[];

    return output;
  };

  return (
    <Wrapper
      label={label}
      required={required}
      name={name}
      error={error}
      tooltip={tooltip}
      helperText={helperText}
      printValue={value.join(", ")}
    >
      <CreatableSelect
        name={name}
        options={optionsWithSelectAll}
        filterOption={
          filterOption ?
            (option, inputValue) => {
              // Always show "Select All" option
              if (option.value === SELECT_ALL_VALUE) {
                return true;
              }
              return filterOption(option, inputValue);
            }
          : undefined
        }
        value={computeValue()}
        onChange={handleChange}
        isDisabled={disabled}
        isMulti={true}
        placeholder={placeholder}
        getOptionLabel={actualFormatOptionLabel}
        getOptionValue={option => option.value}
        styles={{
          control: (
            base: CSSObjectWithLabel,
            _state: ControlProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>(
              { isSelected: false },
              size,
              !!error,
            ).control(base, _state),
          }),
          valueContainer: (
            base: CSSObjectWithLabel,
            _state: ValueContainerProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>(
              { isSelected: false },
              size,
              !!error,
            ).valueContainer(base, _state),
          }),
          input: (
            base: CSSObjectWithLabel,
            _state: InputProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>(
              { isSelected: false },
              size,
              !!error,
            ).input(base, _state),
          }),
          multiValue: (
            base: CSSObjectWithLabel,
            _state: ReactSelectMultiValueProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>({ isSelected: false }, size, !!error)
              .multiValue,
          }),
          multiValueLabel: (
            base: CSSObjectWithLabel,
            _state: MultiValueProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>({ isSelected: false }, size, !!error)
              .multiValueLabel,
          }),
          multiValueRemove: (
            base: CSSObjectWithLabel,
            _state: ReactSelectMultiValueRemoveProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>({ isSelected: false }, size, !!error)
              .multiValueRemove,
          }),
          option: (
            base: CSSObjectWithLabel,
            state: ReactSelectOptionProps<T, true, GroupBase<T>>,
          ) => ({
            ...base,
            ...styledCreatableSelect<T>(
              { isSelected: state.isSelected },
              size,
              !!error,
            ).option,
          }),
        }}
        components={{
          MultiValue: (
            multiValueProps: MultiValueProps<T, true, GroupBase<T>>,
          ) => {
            const extendedProps = multiValueProps as ExtendedMultiValueProps<T>;
            return (
              <CustomMultiValue
                {...extendedProps}
                index={extendedProps.index}
                menuIsOpen={menuIsOpen}
                maxOptionChips={maxOptionChips}
                formatOptionLabel={actualFormatOptionLabel}
              />
            );
          },
          MultiValueRemove,
          Option: (optionProps: OptionProps<T, true>) =>
            CustomOption(optionProps, renderOption),
          ClearIndicator,
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: null,
        }}
        isClearable
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        isLoading={loading}
        onInputChange={handleInputChange}
        isValidNewOption={() => false}
        inputId={name}
        {...props}
      />
    </Wrapper>
  );
};

type ReactHookFormMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Pick<ControllerProps<TFieldValues, TName>, "control" | "name" | "rules"> &
  Omit<MultiSelectProps<BaseOption>, "onChange" | "value"> & {
    onChange?: (value: string[] | BaseOption[] | null) => void;
  };

export const ReactHookFormMultiSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  required = false,
  onChange,
  label,
  tooltip,
  helperText,
  disabled,
  placeholder,
  showSelectAll = true,
  selectAllLabel = "Select all",
  ...props
}: ReactHookFormMultiSelectProps<TFieldValues, TName>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <MultiSelect
          name={name}
          value={field.value || []}
          onChange={field.onChange}
          error={fieldState.error?.message}
          label={label}
          tooltip={tooltip}
          helperText={helperText}
          disabled={disabled}
          placeholder={placeholder}
          showSelectAll={showSelectAll}
          selectAllLabel={selectAllLabel}
          {...props}
        />
      )}
    />
  );
};

export default MultiSelect;
